import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AppwriteStorageService } from '@prism/angular-shared';
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { MergeArrayValues, ProcedureInstanceStatus, ProcedureVersionStatus, PurchaseRequest, SaveStatus } from "@prism/prism-shared";
import { catchError, forkJoin, map, mergeMap, of, pipe, switchMap, withLatestFrom, tap, combineLatest } from "rxjs";
import { PrismCommonActions, PrismCommonSelectors } from ".";
import { PrismCommonApiService } from "../../../services/prism-common-api.service";
import { CoreSelectors } from "../../core";

@Injectable({ providedIn: 'root' })
export class PrismCommonEffects {

    constructor(
        public store: Store,
        private actions$: Actions,
        private prismCommonApiService: PrismCommonApiService,
        private http: HttpClient,
        private storage: AppwriteStorageService,
    ) { }

    private appendTimestamp<T>(
        accessor: (obj: Record<string, T>) => T,
    ) {
        return pipe(
            map(accessor),
            withLatestFrom(this.store.select(CoreSelectors.getCurrentUser)),
            map(([entity, currentUser]) => ({
                ...entity,
                lastSavedOn: new Date(),
                lastSavedById: currentUser.id
            }))
        )
    }

    LoadResources$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadResources.initial),
        mergeMap(() => this.prismCommonApiService.loadResources().pipe(
            map((resources) => PrismCommonActions.LoadResources.success({ resources })),
            catchError((error) => of(PrismCommonActions.LoadResources.failure({ error })))
        ))
    ));

    SetResource$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.SetResource.initial),
        mergeMap(({ resource }) => this.prismCommonApiService.setResource(resource).pipe(
            map(() => PrismCommonActions.SetResource.success({ resource })),
            catchError((error) => of(PrismCommonActions.SetResource.failure({ error })))
        ))
    ));

    LoadResourceById$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadResourceById.initial),
        mergeMap(({ resourceId }) => this.prismCommonApiService.loadResourceById(resourceId).pipe(
            map((resource) => PrismCommonActions.LoadResourceById.success({ resource })),
            catchError((error) => of(PrismCommonActions.LoadResourceById.failure({ error })))
        ))
    ));

    LoadActiveResources$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadActiveResources.initial),
        mergeMap(() => this.prismCommonApiService.loadActiveResources().pipe(
            map((resources) => PrismCommonActions.LoadActiveResources.success({ resources })),
            catchError(({ error }) => of(PrismCommonActions.LoadActiveResources.failure({ error })))
        ))
    ));

    LoadResourcesByIds$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadResourcesByIds.initial),
        mergeMap(({ resourceIds }) => this.prismCommonApiService.loadResourcesByIds(resourceIds).pipe(
            map((resources) => PrismCommonActions.LoadResourcesByIds.success({ resources })),
            catchError(({ error }) => of(PrismCommonActions.LoadResourcesByIds.failure({ error })))
        ))
    ));

    LoadProcedures$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadProcedures.initial),
        mergeMap(() => this.prismCommonApiService.loadProcedures().pipe(
            map((procedures) => PrismCommonActions.LoadProcedures.success({ procedures })),
            catchError(error => of(PrismCommonActions.LoadProcedures.failure({ error })))
        ))
    ));

    LoadProceduresAndHighestReleasedVersion$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadProceduresAndHighestReleasedVersion.initial),
        mergeMap(() => this.prismCommonApiService.loadProceduresAndHighestReleasedVersion().pipe(
            map(({ procedures, procedureVersions }) => PrismCommonActions.LoadProceduresAndHighestReleasedVersion.success({ procedures, procedureVersions })),
            catchError(error => of(PrismCommonActions.LoadProceduresAndHighestReleasedVersion.failure({ error })))
        ))
    ));
    LoadReleasedPlasticsProcedureVersions$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadReleasedPlasticsProcedureVersions.initial),
        mergeMap(() => this.prismCommonApiService.loadReleasedPlasticsProcedureVersions().pipe(
            map(procedureVersions => PrismCommonActions.LoadReleasedPlasticsProcedureVersions.success({ procedureVersions })),
            catchError(error => of(PrismCommonActions.LoadReleasedPlasticsProcedureVersions.failure({ error })))
        ))
    ));

    LoadCompletedProcedureInstancesWithLimit$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadCompletedProcedureInstancesWithLimit.initial),
        mergeMap(({ limit }) => this.prismCommonApiService.loadCompletedProcedureInstancesWithLimit(limit).pipe(
            map((instances) => PrismCommonActions.LoadCompletedProcedureInstancesWithLimit.success({ instances })),
            catchError(error => of(PrismCommonActions.LoadCompletedProcedureInstancesWithLimit.failure({ error })))
        ))
    ))

    LoadProcedureInstancesByStatuses$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadProcedureInstancesByStatuses.initial),
        mergeMap(({ statuses }) => this.prismCommonApiService.loadProcedureInstancesByStatuses(statuses).pipe(
            map((instances) => PrismCommonActions.LoadProcedureInstancesByStatuses.success({ instances })),
            catchError(error => of(PrismCommonActions.LoadProcedureInstancesByStatuses.failure({ error })))
        ))
    ));

    LoadProcedureVersionsByIds$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadProcedureVersionsByIds.initial),
        mergeMap(({ procedureVersionIds }) => this.prismCommonApiService.loadProcedureVersionsByIds(procedureVersionIds).pipe(
            map((procedureVersions) => PrismCommonActions.LoadProcedureVersionsByIds.success({ procedureVersions })),
            catchError(error => of(PrismCommonActions.LoadProcedureVersionsByIds.failure({ error })))
        ))
    ))

    ViewProcedureLoadProcedureData$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.ViewProcedureLoadProcedureData.initial),
        mergeMap(({ procedureId, procedureVersionId }) => this.prismCommonApiService.loadProcedureAndVersionsByProcedureId(procedureId).pipe(
            mergeMap(({ procedure, procedureVersions }) => {
                return this.prismCommonApiService.loadScheduledProceduresByProcedureId(procedure.id).pipe(
                    mergeMap((scheduledProcedures) => {
                        const assetIds: string[] = scheduledProcedures
                            .filter(sp => !!sp.assetId)
                            .map(sp => sp.assetId as string)
                        return this.prismCommonApiService.loadFactoryAssetsByIds(assetIds).pipe(
                            map((factoryAssets) => {
                                return PrismCommonActions.ViewProcedureLoadProcedureData.success({
                                    selectedProcedure: procedure,
                                    selectedProcedureVersions: procedureVersions,
                                    selectedProcedureVersionId: procedureVersionId,
                                    scheduledProcedures,
                                    factoryAssets
                                })
                            }),
                            catchError(error => of(PrismCommonActions.ViewProcedureLoadProcedureData.failure({ error: 'asset failure' })))
                        )
                    }),
                    catchError(error => of(PrismCommonActions.ViewProcedureLoadProcedureData.failure({ error: 'scheduled procedure failure' })))
                )
            }),
            catchError(error => of(PrismCommonActions.ViewProcedureLoadProcedureData.failure({ error: 'procedure and versions failure' })))
        ))
    ))

    SetProcedureVersion$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.SetProcedureVersion.initial),
        mergeMap(({ procedureVersion }) => this.prismCommonApiService.setProcedureVersion(procedureVersion).pipe(
            map(() => PrismCommonActions.SetProcedureVersion.success({ procedureVersion })),
            catchError(error => of(PrismCommonActions.SetProcedureVersion.failure({ error })))
        ))
    ))

    SetProcedure$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.SetProcedure.initial),
        mergeMap(({ procedure }) => this.prismCommonApiService.setProcedure(procedure).pipe(
            map(() => PrismCommonActions.SetProcedure.success({ procedure })),
            catchError(error => of(PrismCommonActions.SetProcedure.failure({ error })))
        ))
    ))

    EditProcedureLoadProcedureData$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.EditProcedureLoadProcedureData.initial),
        mergeMap(({ procedureId, procedureVersionId }) => forkJoin([
            this.prismCommonApiService.loadProcedure(procedureId),
            this.prismCommonApiService.loadProcedureVersion(procedureVersionId),
            this.prismCommonApiService.loadProcedureStepRedlinesByProcedureId(procedureId),
        ]).pipe(
            map(([procedure, procedureVersion, redlines]) => PrismCommonActions.EditProcedureLoadProcedureData.success({
                procedure,
                procedureVersion,
                redlines,
            })),
            catchError(error => of(PrismCommonActions.EditProcedureLoadProcedureData.failure({ error })))
        ))
    ))

    EditProcedureSaveStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                PrismCommonActions.EditProcedure.updatename,
                PrismCommonActions.EditProcedure.updatedescription,
                PrismCommonActions.EditProcedure.createstep,
                PrismCommonActions.EditProcedure.deletestep,
                PrismCommonActions.EditProcedure.updatestep,
                PrismCommonActions.EditProcedure.updatesteps,
                PrismCommonActions.EditProcedure.updateprefacemarkdown,
            ),
            map(() => PrismCommonActions.EditProcedure.updatesavestatus({ saveStatus: SaveStatus.Unsaved }))
        )
    )

    EditProcedureSetProcedureVersion$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.EditProcedureSetProcedureVersion.initial),
        withLatestFrom(this.store.select(PrismCommonSelectors.getEditProcedureProcedureVersion)),
        mergeMap(([_, procedureVersion]) => this.prismCommonApiService.setProcedureVersion(procedureVersion).pipe(
            map(() => PrismCommonActions.EditProcedureSetProcedureVersion.success({ procedureVersion })),
            catchError(error => of(PrismCommonActions.EditProcedureSetProcedureVersion.failure({ error })))
        ))
    ))

    DeleteProcedureVersion$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PrismCommonActions.DeleteProcedureVersion.initial),
            mergeMap(({ versionId }) => this.prismCommonApiService.deleteProcedureVersion(versionId).pipe(
                map(() => PrismCommonActions.DeleteProcedureVersion.success({ versionId })),
                catchError((error) => of(PrismCommonActions.DeleteProcedureVersion.failure({ error })))
            ))
        )
    )

    EditProcedureReleaseProcedureVersion$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.EditProcedureReleaseProcedureVersion.initial),
        withLatestFrom(
            this.store.select(PrismCommonSelectors.getEditProcedureProcedureVersion),
            this.store.select(CoreSelectors.getCurrentUser),
        ),
        mergeMap(([{ releaseComment }, procedureVersion, currentUser]) => {
            return this.prismCommonApiService.getReleasedProcedureVersionCount(procedureVersion.procedureId).pipe(
                switchMap((count) => {
                    return this.prismCommonApiService.setCurrentReleasedProcedureVersionToObsolete(procedureVersion.procedureId).pipe(
                        switchMap(() => {
                            const updatedVersion = structuredClone(procedureVersion);
                            updatedVersion.versionNumber = count + 1;
                            updatedVersion.releaseComment = releaseComment;
                            updatedVersion.releasedById = currentUser.id;
                            updatedVersion.releasedOn = new Date();
                            updatedVersion.status = ProcedureVersionStatus.Released;
                            return this.prismCommonApiService.setProcedureVersion(updatedVersion).pipe(
                                map(() => PrismCommonActions.EditProcedureReleaseProcedureVersion.success({ procedureVersion: updatedVersion })),
                                catchError(error => of(PrismCommonActions.EditProcedureReleaseProcedureVersion.failure({ error })))
                            )
                        })
                    )
                })
            )
        })
    ))

    EditProcedureSetProcedureStepRedline$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.EditProcedureSetProcedureStepRedline.initial),
        mergeMap(({ redline }) => this.prismCommonApiService.setProcedureStepRedline(redline).pipe(
            map(() => PrismCommonActions.EditProcedureSetProcedureStepRedline.success({ redline })),
            catchError(error => of(PrismCommonActions.EditProcedureSetProcedureStepRedline.failure({ error })))
        ))
    ))

    SetProcedureStepRedline$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.SetProcedureStepRedline.initial),
        mergeMap(({ redline }) => this.prismCommonApiService.setProcedureStepRedline(redline).pipe(
            map(() => PrismCommonActions.SetProcedureStepRedline.success({ redline })),
            catchError(error => of(PrismCommonActions.SetProcedureStepRedline.failure({ error })))
        ))
    ))

    SetScheduledProcedure$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.SetScheduledProcedure.initial),
        mergeMap(({ scheduledProcedure }) => this.prismCommonApiService.setScheduledProcedure(scheduledProcedure).pipe(
            map(() => PrismCommonActions.SetScheduledProcedure.success({ scheduledProcedure })),
            catchError(error => of(PrismCommonActions.SetScheduledProcedure.failure({ error })))
        ))
    ))

    DeleteScheduledProcedure$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.DeleteScheduledProcedure.initial),
        mergeMap(({ scheduledProcedure }) => this.prismCommonApiService.deleteScheduledProcedure(scheduledProcedure).pipe(
            map(() => PrismCommonActions.DeleteScheduledProcedure.success({ scheduledProcedure })),
            catchError(error => of(PrismCommonActions.DeleteScheduledProcedure.failure({ error })))
        ))
    ))

    LoadScheduledProcedures$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadScheduledProcedures.initial),
        switchMap(() => this.prismCommonApiService.loadScheduledProcedures().pipe(
            map((scheduledProcedures) => PrismCommonActions.LoadScheduledProcedures.success({ scheduledProcedures })),
            catchError(error => of(PrismCommonActions.LoadScheduledProcedures.failure({ error })))
        ))
    ))


    LoadScheduledProceduresByProcedureId$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadScheduledProceduresByProcedureId.initial),
        mergeMap(({ procedureId }) => this.prismCommonApiService.loadScheduledProceduresByProcedureId(procedureId).pipe(
            map((scheduledProcedures) => PrismCommonActions.LoadScheduledProceduresByProcedureId.success({ scheduledProcedures })),
            catchError(error => of(PrismCommonActions.LoadScheduledProceduresByProcedureId.failure({ error })))
        ))
    ))
    LoadAssetHistoryEventsByAssetId$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadAssetHistoryEventsByAssetId.initial),
        mergeMap(({ assetId }) => this.prismCommonApiService.loadAssetHistoryEventsByAssetId(assetId).pipe(
            map((assetHistoryEvents) => PrismCommonActions.LoadAssetHistoryEventsByAssetId.success({ assetHistoryEvents })),
            catchError(error => of(PrismCommonActions.LoadAssetHistoryEventsByAssetId.failure({ error })))
        ))
    ))

    SetAssetHistoryEvent$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.SetAssetHistoryEvent.initial),
        mergeMap(({ event }) => this.prismCommonApiService.setAssetHistoryEvent(event).pipe(
            map(() => PrismCommonActions.SetAssetHistoryEvent.success({ event })),
            catchError(error => of(PrismCommonActions.SetAssetHistoryEvent.failure({ error })))
        ))
    ))

    LoadScheduledProceduresByProcedureIds$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadScheduledProcedureDataByAssetId.initial),
        mergeMap(({ assetId }) => this.prismCommonApiService.loadScheeduledProceduresByAssetId(assetId).pipe(
            mergeMap((scheduledProcedures) => {
                const procedureIds = scheduledProcedures.map(sp => sp.procedureId);
                return this.prismCommonApiService.loadProceduresAndHighestProcedureVersionsByProcedureIds(procedureIds).pipe(
                    map(({ procedures, procedureVersions }) => PrismCommonActions.LoadScheduledProcedureDataByAssetId.success({
                        scheduledProcedures,
                        procedures,
                        procedureVersions,
                    }))
                )
            }),
            catchError(error => of(PrismCommonActions.LoadScheduledProcedureDataByAssetId.failure({ error })))
        )),
        catchError(error => of(PrismCommonActions.LoadScheduledProcedureDataByAssetId.failure({ error })))
    ))

    SetFactoryAsset$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.SetFactoryAsset.initial),
        mergeMap(({ factoryAsset }) => this.prismCommonApiService.setFactoryAsset(factoryAsset).pipe(
            map(() => PrismCommonActions.SetFactoryAsset.success({ factoryAsset })),
            catchError((error) => of(PrismCommonActions.SetFactoryAsset.failure({ error })))
        ))
    ))

    LoadFactoryAssets$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadFactoryAssets.initial),
        mergeMap(() => this.prismCommonApiService.loadFactoryAssets().pipe(
            map((factoryAssets) => PrismCommonActions.LoadFactoryAssets.success({ factoryAssets })),
            catchError((error) => of(PrismCommonActions.LoadFactoryAssets.failure({ error })))
        ))
    ))

    LoadFactoryAssetsByType$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadFactoryAssetsByType.initial),
        mergeMap(({ assetType }) => this.prismCommonApiService.loadFactoryAssetsByType(assetType).pipe(
            map((factoryAssets) => PrismCommonActions.LoadFactoryAssetsByType.success({ factoryAssets })),
            catchError((error) => of(PrismCommonActions.LoadFactoryAssetsByType.failure({ error })))
        ))
    ))

    LoadFactoryAssetById$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadFactoryAssetById.initial),
        mergeMap(({ assetId }) => this.prismCommonApiService.loadFactoryAssetById(assetId).pipe(
            map((factoryAsset) => PrismCommonActions.LoadFactoryAssetById.success({ factoryAsset })),
            catchError((error) => of(PrismCommonActions.LoadFactoryAssetById.failure({ error })))
        ))
    ))

    LoadResourcesByFactoryAssetId$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadResourceByFactoryAssetId.initial),
        mergeMap(({ assetId }) => this.prismCommonApiService.loadResourcesByFactoryAssetId(assetId).pipe(
            map((resources) => PrismCommonActions.LoadResourceByFactoryAssetId.success({ resources })),
            catchError((error) => of(PrismCommonActions.LoadResourceByFactoryAssetId.failure({ error })))
        ))
    ))

    LoadFactoryAssetsByResourceId$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadFactoryAssetsByResourceId.initial),
        mergeMap(({ resourceId }) => this.prismCommonApiService.loadFactoryAssetsByResourceId(resourceId).pipe(
            map((factoryAssets) => PrismCommonActions.LoadFactoryAssetsByResourceId.success({ factoryAssets })),
            catchError((error) => of(PrismCommonActions.LoadFactoryAssetsByResourceId.failure({ error })))
        ))
    ))

    LoadMappingAssetToResources$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadMappingAssetToResources.initial),
        mergeMap(() => this.prismCommonApiService.loadMappingAssetToResources().pipe(
            map((mappingAssetToResources) => PrismCommonActions.LoadMappingAssetToResources.success({ mappingAssetToResources })),
            catchError((error) => of(PrismCommonActions.LoadMappingAssetToResources.failure({ error })))
        ))
    ))

    LoadMappingAssetToResourcesByAssetId$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadMappingAssetToResourcesByAssetId.initial),
        mergeMap(({ assetId }) => this.prismCommonApiService.loadMappingAssetToResourcesByAssetId(assetId).pipe(
            map((mappingAssetToResources) => PrismCommonActions.LoadMappingAssetToResourcesByAssetId.success({ mappingAssetToResources })),
            catchError((error) => of(PrismCommonActions.LoadMappingAssetToResourcesByAssetId.failure({ error })))
        ))
    ))

    SetMappingAssetToResource$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.SetMappingAssetToResource.initial),
        mergeMap(({ mappingAssetToResource }) => this.prismCommonApiService.setMappingAssetToResource(mappingAssetToResource).pipe(
            map(() => PrismCommonActions.SetMappingAssetToResource.success({ mappingAssetToResource })),
            catchError((error) => of(PrismCommonActions.SetMappingAssetToResource.failure({ error })))
        ))
    ))

    DeleteMappingAssetToResource$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.DeleteMappingAssetToResource.initial),
        mergeMap(({ assetId, resourceId }) => this.prismCommonApiService.getMappingAssetToResourceByAssetAndResourceId(assetId, resourceId).pipe(
            switchMap((mappingAssetToResource) => this.prismCommonApiService.deleteMappingAssetToResource(mappingAssetToResource).pipe(
                map(() => PrismCommonActions.DeleteMappingAssetToResource.success({ mappingAssetToResource })),
                catchError((error) => of(PrismCommonActions.DeleteMappingAssetToResource.failure({ error })))
            ))
        ))
    ))

    LoadReleasedProcedureVersionsByAssetId$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadProceduresAndReleasedVersionsByAssetId.initial),
        mergeMap(({ assetId }) => this.prismCommonApiService.loadReleasedProcedureVersionsByFactoryAssetId(assetId).pipe(
            map(({ procedureVersions, procedures }) => PrismCommonActions.LoadProceduresAndReleasedVersionsByAssetId.success({ procedureVersions, procedures })),
            catchError((error) => of(PrismCommonActions.LoadProceduresAndReleasedVersionsByAssetId.failure({ error })))
        ))
    ))

    LoadMappingAssetToProcedure$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadMappingAssetToProceduresByAssetId.initial),
        mergeMap(({ assetId }) => this.prismCommonApiService.loadMappingAssetToProcedureByAssetId(assetId).pipe(
            map((mappingAssetToProcedures) => PrismCommonActions.LoadMappingAssetToProceduresByAssetId.success({ mappingAssetToProcedures })),
            catchError((error) => of(PrismCommonActions.LoadMappingAssetToProceduresByAssetId.failure({ error })))
        ))
    ))

    SetMappingAssetToProcedure$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.SetMappingAssetToProcedure.initial),
        mergeMap(({ mappingAssetToProcedure }) => this.prismCommonApiService.setMappingAssetToProcedure(mappingAssetToProcedure).pipe(
            map(() => PrismCommonActions.SetMappingAssetToProcedure.success({ mappingAssetToProcedure })),
            catchError((error) => of(PrismCommonActions.SetMappingAssetToProcedure.failure({ error })))
        ))
    ))

    DeleteMappingAssetToProcedure$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.DeleteMappingAssetToProcedure.initial),
        mergeMap(({ assetId, procedureId }) => this.prismCommonApiService.getMappingAssetToProcedureByAssetAndProcedureId(assetId, procedureId).pipe(
            switchMap((mappingAssetToProcedure) => this.prismCommonApiService.deleteMappingAssetToProcedure(mappingAssetToProcedure).pipe(
                map(() => PrismCommonActions.DeleteMappingAssetToProcedure.success({ mappingAssetToProcedure })),
                catchError((error) => of(PrismCommonActions.DeleteMappingAssetToProcedure.failure({ error })))
            ))
        ))
    ))

    LoadBrowseProceduresData$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadBrowseProceduresData.initial),
        mergeMap(() => {
            return forkJoin([
                this.prismCommonApiService.loadCompletedProcedureInstancesWithLimit(5),
                this.prismCommonApiService.loadProcedureInstancesByStatuses([
                    ProcedureInstanceStatus.NotStarted,
                    ProcedureInstanceStatus.InProgress,
                ])
            ]).pipe(
                mergeMap(([completedInstances, instances]) => {
                    const allInstances = [...completedInstances, ...instances];
                    const procedureIds = allInstances.map(instance => instance.procedureId);
                    const versionIds = allInstances.map(instance => instance.procedureVersionId);

                    return this.prismCommonApiService.loadProceduresByIds(procedureIds).pipe(
                        mergeMap((instanceProcedures) => {
                            return this.prismCommonApiService.loadProcedureVersionsByIds(versionIds).pipe(
                                mergeMap((instanceVersions) => {
                                    return this.prismCommonApiService.loadProceduresAndHighestReleasedVersion().pipe(
                                        map(({ procedures, procedureVersions }) => PrismCommonActions.LoadBrowseProceduresData.success({
                                            instances: allInstances,
                                            procedures: MergeArrayValues(procedures, instanceProcedures),
                                            versions: MergeArrayValues(instanceVersions, procedureVersions)
                                        })),
                                        catchError((error) => of(PrismCommonActions.LoadBrowseProceduresData.failure({ error })))
                                    )

                                })

                            )
                        })
                    )
                })
            )
        })
    ))

    SetFreightCarrier$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.SetFreightCarrier.initial),
        mergeMap(({ freightCarrier }) => this.prismCommonApiService.setFreightCarrier(freightCarrier).pipe(
            map(() => PrismCommonActions.SetFreightCarrier.success({ freightCarrier })),
            catchError((error) => of(PrismCommonActions.SetFreightCarrier.failure({ error })))
        ))
    ))

    LoadFreightCarriers$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadFreightCarriers.initial),
        mergeMap(() => this.prismCommonApiService.loadFreightCarriers().pipe(
            map((freightCarriers) => PrismCommonActions.LoadFreightCarriers.success({ freightCarriers })),
            catchError((error) => of(PrismCommonActions.LoadFreightCarriers.failure({ error })))
        ))
    ))

    SetCapitalizationProject$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.SetCapitalizationProject.initial),
        mergeMap(({ project }) => this.prismCommonApiService.setCapitalizationProject(project).pipe(
            map(() => PrismCommonActions.SetCapitalizationProject.success({ project })),
            catchError((error) => of(PrismCommonActions.SetCapitalizationProject.failure({ error })))
        ))
    ))

    LoadCapitalizationProjects$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadCapitalizationProjects.initial),
        mergeMap(() => this.prismCommonApiService.loadCapitalizationProjects().pipe(
            map((projects) => PrismCommonActions.LoadCapitalizationProjects.success({ projects })),
            catchError((error) => of(PrismCommonActions.LoadCapitalizationProjects.failure({ error })))
        ))
    ))

    LoadCustomers$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadCustomers.initial),
        mergeMap(() => this.prismCommonApiService.loadCustomers().pipe(
            map((customers) => PrismCommonActions.LoadCustomers.success({ customers })),
            catchError((error) => of(PrismCommonActions.LoadCustomers.failure({ error })))
        ))
    ));

    LoadCustomerById$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadCustomerById.initial),
        mergeMap(({ customerId }) => this.prismCommonApiService.loadCustomerById(customerId).pipe(
            map((customer) => PrismCommonActions.LoadCustomerById.success({ customer })),
            catchError((error) => of(PrismCommonActions.LoadCustomerById.failure({ error })))
        ))
    ));

    LoadCustomerByIds$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadCustomersByIds.initial),
        mergeMap(({ customerIds }) => this.prismCommonApiService.loadCustomersByIds(customerIds).pipe(
            map((customers) => PrismCommonActions.LoadCustomersByIds.success({ customers })),
            catchError((error) => of(PrismCommonActions.LoadCustomersByIds.failure({ error })))
        ))
    ));

    SetCustomer$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.SetCustomer.initial),
        mergeMap(({ customer }) => this.prismCommonApiService.setCustomer(customer).pipe(
            map(() => PrismCommonActions.SetCustomer.success({ customer })),
            catchError((error) => of(PrismCommonActions.SetCustomer.failure({ error })))
        ))
    ));

    LoadPlasticsCustomers$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadPlasticsCustomers.initial),
        mergeMap(() => this.prismCommonApiService.loadPlasticsCustomers().pipe(
            map((customers) => PrismCommonActions.LoadPlasticsCustomers.success({ customers })),
            catchError((error) => of(PrismCommonActions.LoadPlasticsCustomers.failure({ error })))
        ))
    ))

    LoadMetalsCustomers$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadMetalsCustomers.initial),
        mergeMap(() => this.prismCommonApiService.loadMetalsCustomers().pipe(
            map((customers) => PrismCommonActions.LoadMetalsCustomers.success({ customers })),
            catchError((error) => of(PrismCommonActions.LoadMetalsCustomers.failure({ error })))
        ))
    ))

    LoadC3Customers$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadC3Customers.initial),
        mergeMap(() => this.prismCommonApiService.loadC3Customers().pipe(
            map((customers) => PrismCommonActions.LoadC3Customers.success({ customers })),
            catchError((error) => of(PrismCommonActions.LoadC3Customers.failure({ error })))
        ))
    ))

    LoadVendors$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadVendors.initial),
        mergeMap(() => this.prismCommonApiService.loadVendors().pipe(
            map((vendors) => PrismCommonActions.LoadVendors.success({ vendors })),
            catchError((error) => of(PrismCommonActions.LoadVendors.failure({ error })))
        ))
    ));

    LoadPlasticsVendors$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadPlasticsVendors.initial),
        mergeMap(() => this.prismCommonApiService.loadPlasticsVendors().pipe(
            map((vendors) => PrismCommonActions.LoadPlasticsVendors.success({ vendors })),
            catchError((error) => of(PrismCommonActions.LoadPlasticsVendors.failure({ error })))
        ))
    ));

    LoadMetalsVendors$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadMetalsVendors.initial),
        mergeMap(() => this.prismCommonApiService.loadMetalsVendors().pipe(
            map((vendors) => PrismCommonActions.LoadMetalsVendors.success({ vendors })),
            catchError((error) => of(PrismCommonActions.LoadMetalsVendors.failure({ error })))
        ))
    ));

    LoadC3Vendors$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadC3Vendors.initial),
        mergeMap(() => this.prismCommonApiService.loadC3Vendors().pipe(
            map((vendors) => PrismCommonActions.LoadC3Vendors.success({ vendors })),
            catchError((error) => of(PrismCommonActions.LoadC3Vendors.failure({ error })))

        ))
    ))

    LoadAutomationVendors$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadAutomationVendors.initial),
        mergeMap(() => this.prismCommonApiService.loadAutomationVendors().pipe(
            map((vendors) => PrismCommonActions.LoadAutomationVendors.success({ vendors })),
            catchError((error) => of(PrismCommonActions.LoadAutomationVendors.failure({ error })))
        ))
    ))

    LoadVendorById$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadVendorById.initial),
        mergeMap(({ vendorId }) => this.prismCommonApiService.loadVendorById(vendorId).pipe(
            map((vendor) => PrismCommonActions.LoadVendorById.success({ vendor })),
            catchError((error) => of(PrismCommonActions.LoadVendorById.failure({ error })))
        ))
    ));

    LoadVendorsByIds$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadVendorsByIds.initial),
        mergeMap(({ vendorIds }) => this.prismCommonApiService.loadVendorsByIds(vendorIds).pipe(
            map((vendors) => PrismCommonActions.LoadVendorsByIds.success({ vendors })),
            catchError((error) => of(PrismCommonActions.LoadVendorsByIds.failure({ error })))
        ))
    ));

    SetVendor$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.SetVendor.initial),
        mergeMap(({ vendor }) => this.prismCommonApiService.setVendor(vendor).pipe(
            map(() => PrismCommonActions.SetVendor.success({ vendor })),
            catchError((error) => of(PrismCommonActions.SetVendor.failure({ error })))
        ))
    ));

    SetPurchaseRequestList$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.SetPurchaseRequestList.initial),
        mergeMap(({ purchaseRequestList }) => this.prismCommonApiService.setPurchaseRequestList(purchaseRequestList).pipe(
            map(() => PrismCommonActions.SetPurchaseRequestList.success({ purchaseRequestList })),
            catchError((error) => of(PrismCommonActions.SetPurchaseRequestList.failure({ error })))
        ))
    ))

    LoadPurchaseRequestLists$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadPurchaseRequestLists.initial),
        mergeMap(() => this.prismCommonApiService.loadPurchaseRequestLists().pipe(
            map((purchaseRequestLists) => PrismCommonActions.LoadPurchaseRequestLists.success({ purchaseRequestLists })),
            catchError((error) => of(PrismCommonActions.LoadPurchaseRequestLists.failure({ error })))
        ))
    ))

    LoadPurchaseRequestListById$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadPurchaseRequestListById.initial),
        mergeMap(({ purchaseRequestListId }) => this.prismCommonApiService.loadPurchaseRequestListById(purchaseRequestListId).pipe(
            map((purchaseRequestList) => PrismCommonActions.LoadPurchaseRequestListById.success({ purchaseRequestList })),
            catchError((error) => of(PrismCommonActions.LoadPurchaseRequestListById.failure({ error })))
        ))
    ))

    LoadPurchaseRequestListsByIds$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadPurchaseRequestListsByIds.initial),
        mergeMap(({ purchaseRequestListIds }) => this.prismCommonApiService.loadPurchaseRequestListsByIds(purchaseRequestListIds).pipe(
            map((purchaseRequestLists) => PrismCommonActions.LoadPurchaseRequestListsByIds.success({ purchaseRequestLists })),
            catchError((error) => of(PrismCommonActions.LoadPurchaseRequestListsByIds.failure({ error })))
        ))
    ))

    LoadPurchaseRequestListsWithFollowerId$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadPurchaseRequestListsWithFollowerId.initial),
        mergeMap(({ userId }) => this.prismCommonApiService.loadPurchaseRequestListsWithFollowerId(userId).pipe(
            map((purchaseRequestLists) => PrismCommonActions.LoadPurchaseRequestListsWithFollowerId.success({ purchaseRequestLists })),
            catchError((error) => of(PrismCommonActions.LoadPurchaseRequestListsWithFollowerId.failure({ error })))
        ))
    ))

    SetPurchaseRequest$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.SetPurchaseRequest.initial),
        mergeMap(({ purchaseRequest }) => this.prismCommonApiService.setPurchaseRequest(purchaseRequest).pipe(
            map(() => PrismCommonActions.SetPurchaseRequest.success({ purchaseRequest })),
            catchError((error) => of(PrismCommonActions.SetPurchaseRequest.failure({ error })))
        ))
    ))

    UpdatePurchaseRequestFiles$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.UpdatePurchaseRequestFiles.initial),
        mergeMap(({ requestId, files }) => this.prismCommonApiService.updatePurchaseRequest(requestId, { files }).pipe(
            map(() => PrismCommonActions.UpdatePurchaseRequestFiles.success({ requestId, files })),
            catchError((error) => of(PrismCommonActions.UpdatePurchaseRequestFiles.failure({ error })))
        ))
    ))

    SetPurchaseRequests$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.SetPurchaseRequests.initial),
        mergeMap(({ purchaseRequests }) => this.prismCommonApiService.setPurchaseRequests(purchaseRequests).pipe(
            map(() => PrismCommonActions.SetPurchaseRequests.success({ purchaseRequests })),
            catchError((error) => of(PrismCommonActions.SetPurchaseRequests.failure({ error })))
        ))
    ))

    LoadPurchaseRequests$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadPurchaseRequests.initial),
        mergeMap(() => this.prismCommonApiService.loadPurchaseRequests().pipe(
            map((purchaseRequests) => PrismCommonActions.LoadPurchaseRequests.success({ purchaseRequests })),
            catchError((error) => of(PrismCommonActions.LoadPurchaseRequests.failure({ error })))
        ))
    ))

    LoadPurchaseRequestsByPurchaseRequestListId$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadPurchaseRequestsByPurchaseRequestListId.initial),
        mergeMap(({ purchaseRequestListId }) => this.prismCommonApiService.loadPurchaseRequestsByPurchaseRequestListId(purchaseRequestListId).pipe(
            map((purchaseRequests) => PrismCommonActions.LoadPurchaseRequestsByPurchaseRequestListId.success({ purchaseRequests })),
            catchError((error) => of(PrismCommonActions.LoadPurchaseRequestsByPurchaseRequestListId.failure({ error })))
        ))
    ))

    LoadPurchaseRequestById$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadPurchaseRequestById.initial),
        mergeMap(({ purchaseRequestId }) => this.prismCommonApiService.loadPurchaseRequestById(purchaseRequestId).pipe(
            map((purchaseRequest) => PrismCommonActions.LoadPurchaseRequestById.success({ purchaseRequest })),
            catchError((error) => of(PrismCommonActions.LoadPurchaseRequestById.failure({ error })))
        ))
    ))

    LoadIncompletePurchaseRequests$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadIncompletePurchaseRequests.initial),
        mergeMap(() => this.prismCommonApiService.loadIncompletePurchaseRequests().pipe(
            map((purchaseRequests) => PrismCommonActions.LoadIncompletePurchaseRequests.success({ purchaseRequests })),
            catchError((error) => of(PrismCommonActions.LoadIncompletePurchaseRequests.failure({ error })))
        ))
    ))

    // LoadOrderedPurchaseRequestsByHardwareId$ = createEffect(() => this.actions$.pipe(
    //     ofType(PrismCommonActions.LoadOrderedPurchaseRequestsByHardwareId.initial),
    //     mergeMap(({ hardwareItemId }) => this.prismCommonApiService.loadOrderedPurchaseRequestsByHardwareItemId(hardwareItemId).pipe(
    //         map((purchaseRequests) => PrismCommonActions.LoadOrderedPurchaseRequestsByHardwareId.success({ purchaseRequests })),
    //         catchError((error) => of(PrismCommonActions.LoadOrderedPurchaseRequestsByHardwareId.failure({ error })))
    //     ))
    // ))

    LoadContacts$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadContacts.initial),
        mergeMap(() => this.prismCommonApiService.loadContacts().pipe(
            map((contacts) => PrismCommonActions.LoadContacts.success({ contacts })),
            catchError((error) => of(PrismCommonActions.LoadContacts.failure({ error })))
        ))
    ))

    SetContact$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.SetContact.initial),
        mergeMap(({ contact }) => this.prismCommonApiService.setContact(contact).pipe(
            map(() => PrismCommonActions.SetContact.success({ contact })),
            catchError((error) => of(PrismCommonActions.SetContact.failure({ error })))
        ))
    ))

    LoadContactById$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadContactById.initial),
        mergeMap(({ contactId }) => this.prismCommonApiService.loadContactById(contactId).pipe(
            map((contact) => PrismCommonActions.LoadContactById.success({ contact })),
            catchError((error) => of(PrismCommonActions.LoadContactById.failure({ error })))
        ))
    ))

    LoadContactAssociationByCustomerId$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadContactAssociationByCustomerId.initial),
        mergeMap(({ customerId }) => this.prismCommonApiService.loadContactAssociationByCustomerId(customerId).pipe(
            map((contacts) => PrismCommonActions.LoadContactAssociationByCustomerId.success({ contacts })),
            catchError((error) => of(PrismCommonActions.LoadContactAssociationByCustomerId.failure({ error })))
        ))
    ))

    LoadSoftwareRequests$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadSoftwareRequests.initial),
        mergeMap(() => this.prismCommonApiService.loadSoftwareRequests().pipe(
            map((requests) => PrismCommonActions.LoadSoftwareRequests.success({ requests })),
            catchError((error) => of(PrismCommonActions.LoadSoftwareRequests.failure({ error })))
        ))
    ))

    LoadSoftwareRequestById$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.LoadSoftwareRequestById.initial),
        mergeMap(({ requestId }) => this.prismCommonApiService.loadSoftwareRequestById(requestId).pipe(
            map((request) => PrismCommonActions.LoadSoftwareRequestById.success({ request })),
            catchError((error) => of(PrismCommonActions.LoadSoftwareRequestById.failure({ error })))
        ))
    ))

    SetSoftwareRequest$ = createEffect(() => this.actions$.pipe(
        ofType(PrismCommonActions.SetSoftwareRequest.initial),
        mergeMap(({ request }) => this.prismCommonApiService.setSoftwareRequest(request).pipe(
            map(() => PrismCommonActions.SetSoftwareRequest.success({ request })),
            catchError((error) => of(PrismCommonActions.SetSoftwareRequest.failure({ error })))
        ))
    ))

}